import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["disableOutsidePointerEvents", "onEscapeKeyDown", "onPointerDownOutside", "onFocusOutside", "onInteractOutside", "onDismiss"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { CONTEXT_UPDATE } from './constants';
import { dispatchUpdate, useFocusOutside } from './useFocusOutside';
import { usePointerDownOutside } from './usePointerDownOutside';
import { DomPrimitive } from '../DomPrimitive';
import { useComposedRefs } from '../../hooks/useComposedRefs';
import { useEscapeKeydown } from '../../hooks/useEscapeKeyodwn';
import { composeEventHandlers } from '../../utils/composeEventHandlers';
import { jsx as _jsx } from "react/jsx-runtime";
var DISMISSABLE_LAYER_NAME = 'DismissableLayer';
var originalBodyPointerEvents;
var DismissableLayerContext = /*#__PURE__*/React.createContext({
  layers: new Set(),
  layersWithOutsidePointerEventsDisabled: new Set(),
  branches: new Set()
});
export var DismissableLayer = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var _node$ownerDocument;
  var _props$disableOutside = props.disableOutsidePointerEvents,
    disableOutsidePointerEvents = _props$disableOutside === void 0 ? false : _props$disableOutside,
    onEscapeKeyDown = props.onEscapeKeyDown,
    onPointerDownOutside = props.onPointerDownOutside,
    onFocusOutside = props.onFocusOutside,
    onInteractOutside = props.onInteractOutside,
    onDismiss = props.onDismiss,
    layerProps = _objectWithoutProperties(props, _excluded);
  var context = React.useContext(DismissableLayerContext);
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    node = _React$useState2[0],
    setNode = _React$useState2[1];
  var ownerDocument = (_node$ownerDocument = node === null || node === void 0 ? void 0 : node.ownerDocument) !== null && _node$ownerDocument !== void 0 ? _node$ownerDocument : globalThis === null || globalThis === void 0 ? void 0 : globalThis.document;
  var _React$useState3 = React.useState({}),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    force = _React$useState4[1];
  var composedRefs = useComposedRefs(forwardedRef, function (node) {
    return setNode(node);
  });
  var layers = Array.from(context.layers);
  var _slice = _toConsumableArray(context.layersWithOutsidePointerEventsDisabled).slice(-1),
    _slice2 = _slicedToArray(_slice, 1),
    highestLayerWithOutsidePointerEventsDisabled = _slice2[0]; // prettier-ignore
  var highestLayerWithOutsidePointerEventsDisabledIndex = layers.indexOf(highestLayerWithOutsidePointerEventsDisabled); // prettier-ignore
  var index = node ? layers.indexOf(node) : -1;
  var isBodyPointerEventsDisabled = context.layersWithOutsidePointerEventsDisabled.size > 0;
  var isPointerEventsEnabled = index >= highestLayerWithOutsidePointerEventsDisabledIndex;
  var pointerDownOutside = usePointerDownOutside(function (event) {
    var target = event.target;
    var isPointerDownOnBranch = _toConsumableArray(context.branches).some(function (branch) {
      return branch.contains(target);
    });
    if (!isPointerEventsEnabled || isPointerDownOnBranch) {
      return;
    }
    onPointerDownOutside === null || onPointerDownOutside === void 0 || onPointerDownOutside(event);
    onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
    if (!event.defaultPrevented) {
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }
  }, ownerDocument);
  var focusOutside = useFocusOutside(function (event) {
    var target = event.target;
    var isFocusInBranch = _toConsumableArray(context.branches).some(function (branch) {
      return branch.contains(target);
    });
    if (isFocusInBranch) {
      return;
    }
    onFocusOutside === null || onFocusOutside === void 0 || onFocusOutside(event);
    onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
    if (!event.defaultPrevented) {
      onDismiss === null || onDismiss === void 0 || onDismiss();
    }
  }, ownerDocument);
  useEscapeKeydown(function (event) {
    var isHighestLayer = index === context.layers.size - 1;
    if (!isHighestLayer) {
      return;
    }
    onEscapeKeyDown === null || onEscapeKeyDown === void 0 || onEscapeKeyDown(event);
    if (!event.defaultPrevented && onDismiss) {
      event.preventDefault();
      onDismiss();
    }
  }, ownerDocument);
  React.useEffect(function () {
    if (!node) {
      return;
    }
    if (disableOutsidePointerEvents) {
      if (context.layersWithOutsidePointerEventsDisabled.size === 0) {
        originalBodyPointerEvents = ownerDocument.body.style.pointerEvents;
        ownerDocument.body.style.pointerEvents = 'none';
      }
      context.layersWithOutsidePointerEventsDisabled.add(node);
    }
    context.layers.add(node);
    dispatchUpdate();
    return function () {
      if (disableOutsidePointerEvents && context.layersWithOutsidePointerEventsDisabled.size === 1) {
        ownerDocument.body.style.pointerEvents = originalBodyPointerEvents;
      }
    };
  }, [node, ownerDocument, disableOutsidePointerEvents, context]);

  /**
   * Мы намеренно предотвращаем объединение этого эффекта с эффектом `disableOutsidePointerEvents`
   * потому что изменение `disableOutsidePointerEvents` удалило бы этот слой из стека
   * и добавило его снова в конец, так что порядок слоев не был бы _порядком создания_.
   * Мы хотим, чтобы они удалялись из контекстных стеков только при размонтировании.
   */
  React.useEffect(function () {
    return function () {
      if (!node) {
        return;
      }
      context.layers.delete(node);
      context.layersWithOutsidePointerEventsDisabled.delete(node);
      dispatchUpdate();
    };
  }, [node, context]);
  React.useEffect(function () {
    var handleUpdate = function handleUpdate() {
      return force({});
    };
    document.addEventListener(CONTEXT_UPDATE, handleUpdate);
    return function () {
      return document.removeEventListener(CONTEXT_UPDATE, handleUpdate);
    };
  }, []);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({}, layerProps), {}, {
    ref: composedRefs,
    style: _objectSpread({
      pointerEvents: isBodyPointerEventsDisabled ? isPointerEventsEnabled ? 'auto' : 'none' : undefined
    }, props.style),
    onFocusCapture: composeEventHandlers(props.onFocusCapture, focusOutside.onFocusCapture),
    onBlurCapture: composeEventHandlers(props.onBlurCapture, focusOutside.onBlurCapture),
    onPointerDownCapture: composeEventHandlers(props.onPointerDownCapture, pointerDownOutside.onPointerDownCapture)
  }));
});
DismissableLayer.displayName = DISMISSABLE_LAYER_NAME;

/* -------------------------------------------------------------------------------------------------
 * DismissableLayerBranch
 * -----------------------------------------------------------------------------------------------*/

var BRANCH_NAME = 'DismissableLayerBranch';
var DismissableLayerBranch = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var context = React.useContext(DismissableLayerContext);
  var ref = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, ref);
  React.useEffect(function () {
    var node = ref.current;
    if (node) {
      context.branches.add(node);
      return function () {
        context.branches.delete(node);
      };
    }
    return function () {};
  }, [context.branches]);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({}, props), {}, {
    ref: composedRefs
  }));
});
DismissableLayerBranch.displayName = BRANCH_NAME;